import axios from 'axios';

import 'ol/ol.css';
import { get as getProjection } from 'ol/proj';
import * as olLayer from 'ol/layer';
import * as olSource from 'ol/source';
import * as olFormat from 'ol/format';
import * as olTileGrid from 'ol/tilegrid';
import * as olExtent from 'ol/extent';

const geoServer = process.env.VUE_APP_GEO_SERVER_URL;

// 1. Define VWorld API Key and Service URL
const vworldApiKey = '2FE375C7-C121-3902-8BF8-31F92B96454E';

const DotMapSolarContext = {

  getBaseLayers(opts) {
    const layers = new Array();

    // console.log('map context base layers', opts);
    if (opts.baseOn) {
      layers.push(new olLayer.Tile({
        source: new olSource.XYZ({
          url: `https://api.vworld.kr/req/wmts/1.0.0/${vworldApiKey}/Base/{z}/{y}/{x}.jpeg`,
          properties: { name: 'base-vworld-base' },
          minZoom: 5,
          maxZoom: 19,
          zIndex: 2,
          preload: Infinity
        })
      }));
    }
    if (opts.satelliteOn) {
      layers.push(new olLayer.Tile({
        source: new olSource.XYZ({
          url: `https://api.vworld.kr/req/wmts/1.0.0/${vworldApiKey}/Satellite/{z}/{y}/{x}.jpeg`,
          properties: { name: 'base-vworld-satellite' },
          minZoom: 5,
          maxZoom: 19,
          zIndex: 2,
          preload: Infinity
        })
      }));
    }

    return layers;

  },

  getSiteImageMapTileLayer() {
    return new olLayer.Tile({
      source: new olSource.TileWMS({
        // crossOrigin: 'anonymous'
        url: geoServer + '/wms',
        params: {
          'FORMAT': 'image/png',
          'VERSION': '1.1.1',
          'tiled': true,
          'LAYERS': 'solar-plant:site_image_grp_layer',
          'SRS': 'EPSG:4326',
          'exceptions': 'application/vnd.ogc.se_inimage'
        }
      })
    });
  },

  async getSolarModuleLayer(siteSeq, reportSeq, style) {
    const response = await axios.get(geoServer + '/wfs', {
      params: {
        service: 'WFS',
        version: '1.1.1',
        request: 'GetFeature',
        typename: 'solar-plant:solar_module',
        outputFormat: 'application/json',
        exceptions: 'application/json',
        srsname: 'EPSG:4326',
        viewparams: 'site_seq:' + siteSeq + ';report_seq:' + reportSeq
      },
      headers: {
        'Content-Type': 'application/json'
      }
    });
    const raw = response.data;
    const totalReturned = raw?.numberReturned ?? raw?.totalFeatures ?? raw?.features?.length;
    const features = new olFormat.GeoJSON().readFeatures(raw, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:4326'
    });
    const withGeometry = features.filter(f => f.getGeometry());

    if (withGeometry.length > 0) {
      const sample = withGeometry[0];
      const sampleGeom = sample.getGeometry();
      const sampleProps = sample.getProperties();
      console.log('[SolarModule WFS] loaded', {
        siteSeq,
        reportSeq,
        numberReturned: totalReturned,
        parsed: features.length,
        withGeometry: withGeometry.length,
        sampleGeometryType: sampleGeom?.getType?.(),
        sampleExtent: sampleGeom?.getExtent?.(),
        samplePropertyKeys: Object.keys(sampleProps).filter(k => k !== 'geometry'),
        sampleModuleFields: {
          solar_module_seq: sampleProps.solar_module_seq,
          module_id: sampleProps.module_id,
          array_id: sampleProps.array_id,
          string_id: sampleProps.string_id,
          has_defect: sampleProps.has_defect,
          defect_type: sampleProps.defect_type
        }
      });
    } else {
      console.warn('[SolarModule WFS] 피처는 있으나 geometry 없음', {
        siteSeq,
        reportSeq,
        numberReturned: totalReturned,
        parsed: features.length
      });
    }

    if (withGeometry.length === 0) {
      return undefined;
    }

    const source = new olSource.Vector({
      features: withGeometry,
      wrapX: false
    });
    const layerExtent = source.getExtent();
    console.log('[SolarModule WFS] layer extent', layerExtent);

    const heavyLayer = withGeometry.length > 200;

    return new olLayer.Vector({
      source,
      style,
      zIndex: 25,
      // 피처 수천 개일 때 vector 즉시 렌더 + fit 조합이 뷰·타일을 깨뜨릴 수 있음
      renderMode: heavyLayer ? 'image' : 'vector',
      updateWhileAnimating: !heavyLayer,
      updateWhileInteracting: !heavyLayer,
      properties: { layerKind: 'solar_module' }
    });
  },

  getSolarRgbImageLayer(examineReportSeq, style) {
    return new olLayer.Vector({
      source: new olSource.Vector({
        format: new olFormat.GeoJSON(),
        url: geoServer + '/wfs' +
          '?service=WFS' +
          '&version=1.1.1' +
          '&request=GetFeature' +
          '&typename=solar-plant:solar_rgb_image' +
          '&outputFormat=application/json' +
          '&exceptions=application/json' +
          '&srsname=EPSG:4326' +
          '&viewparams=report_seq:' + examineReportSeq
      }),
      style: style
    });
  },

  getRgbImageFeatureCatalogLayer(examineReportSeq, style) {
    return new olLayer.Vector({
      source: new olSource.Vector({
        format: new olFormat.GeoJSON(),
        url: geoServer + '/wfs' +
          '?service=WFS' +
          '&version=1.1.1' +
          '&request=GetFeature' +
          '&typename=solar-plant:rgb_image_feature_catalog' +
          '&outputFormat=application/json' +
          '&exceptions=application/json' +
          '&srsname=EPSG:4326' +
          '&viewparams=report_seq:' + examineReportSeq
      }),
      style: style
    });
  },

  getThermalImageFeatureCatalogLayer(examineReportSeq, style) {
    return new olLayer.Vector({
      source: new olSource.Vector({
        format: new olFormat.GeoJSON(),
        url: geoServer + '/wfs' +
          '?service=WFS' +
          '&version=1.1.1' +
          '&request=GetFeature' +
          '&typename=solar-plant:thermal_image_feature_catalog' +
          '&outputFormat=application/json' +
          '&exceptions=application/json' +
          '&srsname=EPSG:4326' +
          '&viewparams=report_seq:' + examineReportSeq
      }),
      style: style
    });
  },

  getSolarModuleDefectLayer(examineReportSeq, defectFilters, defectStyle) {

    let url = '/wfs' +
      '?service=WFS' +
      '&version=1.1.1' +
      '&request=GetFeature' +
      '&typename=solar-plant:module_defects_bytypes' +
      '&outputFormat=application/json' +
      '&exceptions=application/json' +
      '&srsname=EPSG:4326' +
      '&viewparams=report_seq:' + examineReportSeq;

    console.log('defect codes for WFS', defectFilters);

    if (defectFilters.length > 0) {
      let filterParams = '';
      defectFilters.forEach(code => {
        filterParams += '\'' + code + '\'\\,';
      });
      url += ';defect_types:' + encodeURIComponent(filterParams.slice(0, -2));
    }

    return new olLayer.Vector({
      source: new olSource.Vector({
        format: new olFormat.GeoJSON(),
        url: geoServer + url

      }),
      style: defectStyle
    });
  },

  getSolarModuleDefectLayer2(examineReportSeq, defectFilters, defectStyle) {

    let url = '/wfs' +
      '?service=WFS' +
      '&version=1.1.1' +
      '&request=GetFeature' +
      '&typename=solar-plant:module_defects_by_new_type' +
      '&outputFormat=application/json' +
      '&exceptions=application/json' +
      '&srsname=EPSG:4326' +
      '&viewparams=report_seq:' + examineReportSeq;

    // console.log('defect codes for WFS', defectFilters);

    if (defectFilters.length > 0) {
      let filterParams = '';
      defectFilters.forEach(code => {
        filterParams += code + '\\,';
      });
      url += ';defect_codes:' + encodeURIComponent(filterParams.slice(0, -2));
    }

    console.log('[DEFECT-MAP-DEBUG] getSolarModuleDefectLayer2 WFS URL', {
      reportSeq: examineReportSeq,
      defectCodeCount: defectFilters.length,
      fullUrl: geoServer + url
    });

    return new olLayer.Vector({
      source: new olSource.Vector({
        format: new olFormat.GeoJSON(),
        url: geoServer + url

      }),
      style: defectStyle
    });
  },
  /**
   * 결함정보 가져오기 (AI)
   */
  getSolarModuleAIDefectLayer(examineReportSeq, defectStyle) {

    const url = '/wfs' +
      '?service=WFS' +
      '&version=1.1.1' +
      '&request=GetFeature' +
      '&typename=solar-plant:ai_detect_defects' +
      '&outputFormat=application/json' +
      '&exceptions=application/json' +
      '&srsname=EPSG:4326' +
      '&viewparams=report_seq:' + examineReportSeq;

    // console.log('defect codes for WFS', defectFilters);

    return new olLayer.Vector({
      source: new olSource.Vector({
        format: new olFormat.GeoJSON(),
        url: geoServer + url

      }),
      style: defectStyle
    });
  },

  /**
   * AI 검출 결함의 지도 좌표를 레이어 없이 직접 조회 — dedup 클러스터링용.
   * (AI 마커 레이어가 꺼져 있어도 사용 가능해야 하므로 WFS를 직접 호출한다.)
   * @param {string|number} examineReportSeq
   * @returns {Promise<Array<{ seq: number, lng: number, lat: number, defectType: string, imgName: string, thermalImgSeq: number, mlDefectConfirmed: string|null }>>}
   */
  async fetchAiDetectDefectGeoRows(examineReportSeq) {
    const response = await axios.get(geoServer + '/wfs', {
      params: {
        service: 'WFS',
        version: '1.1.1',
        request: 'GetFeature',
        typename: 'solar-plant:ai_detect_defects',
        outputFormat: 'application/json',
        exceptions: 'application/json',
        srsname: 'EPSG:4326',
        viewparams: 'report_seq:' + examineReportSeq
      },
      headers: { 'Content-Type': 'application/json' }
    });
    const features = Array.isArray(response.data?.features)
      ? response.data.features
      : [];
    return features
      .map(f => {
        const p = f?.properties || {};
        const c = f?.geometry?.coordinates;
        return {
          seq: Number(p.seq),
          lng: Array.isArray(c) ? Number(c[0]) : NaN,
          lat: Array.isArray(c) ? Number(c[1]) : NaN,
          defectType: p.defect_type ?? '',
          imgName: p.img_name ?? '',
          thermalImgSeq: p.thermal_img_seq,
          mlDefectConfirmed: p.ml_defect_confirmed ?? null
        };
      })
      .filter(
        r => Number.isFinite(r.seq) && Number.isFinite(r.lng) && Number.isFinite(r.lat)
      );
  },

  getSolarModuleFeatures(siteSeq) {
    // return new olSource.Vector({
    // 	format: new olFormat.GeoJSON(),
    // 	url: geoServer + '/geoserver/wfs' +
    // '?service=WFS' +
    // '&version=1.1.1' +
    // '&request=GetFeature' +
    // '&typename=solar-plant:solar_module' +
    // '&outputFormat=application/json' +
    // '&exceptions=application/json' +
    // '&srsname=EPSG:4326' +
    // '&viewparams=site_seq:' + siteSeq
    // })
    return axios({
      method: 'get',
      crossOrigin: 'anonymous',
      url: geoServer + '/wfs' +
        '?service=WFS' +
        '&version=1.1.1' +
        '&request=GetFeature' +
        '&typename=solar-plant:solar_module' +
        '&outputFormat=application/json' +
        '&exceptions=application/json' +
        '&srsname=EPSG:4326' +
        '&viewparams=site_seq:' + siteSeq
    });
  },

  getRgbImgFeatures(examineReportSeq) {
    return axios({
      method: 'get',
      crossOrigin: 'anonymous',
      url: geoServer + '/wfs' +
        '?service=WFS' +
        '&version=1.1.1' +
        '&request=GetFeature' +
        '&typename=solar-plant:solar_rgb_image' +
        '&outputFormat=application/json' +
        '&exceptions=application/json' +
        '&srsname=EPSG:4326' +
        '&viewparams=report_seq:' + examineReportSeq
    });
  },

  getRgbImgFeature(imgSeq) {
    const viewParams = 'img_seq:' + imgSeq;
    return this.getFeature('solar-plant:rgb_img_feature', viewParams);
  },

  getThermalImgFeature(imgSeq) {
    const viewParams = 'img_seq:' + imgSeq;
    return this.getFeature('solar-plant:thermal_img_feature', viewParams);
  },

  getFeature(layerName, viewParams) {
    const featureUrl = geoServer + '/wfs' +
      '?service=WFS' +
      '&version=1.1.1' +
      '&request=GetFeature' +
      '&typename=' + layerName +
      '&outputFormat=application/json' +
      '&exceptions=application/json' +
      '&srsname=EPSG:4326' +
      '&viewparams=' + viewParams;
    return axios({
      url: featureUrl,
      method: 'GET'
    });

  },


  forEachFeature(evt, callback) {

    const coordinate = evt.coordinate;
    const resolution = evt.map.getView().getResolution();
    const projection = evt.map.getView().getProjection();
    const params = {
      'INFO_FORMAT': 'application/json',
      'FEATURE_COUNT': 50
    };

    //VectorLayers
    evt.map.forEachFeatureAtPixel(evt.pixel, callback);

    //TileLayers
    evt.map.getLayers().forEach(async (layer) => {

      if (layer.getSource() != undefined && layer.getSource().getFeatureInfoUrl != undefined) {
        const url = layer.getSource().getFeatureInfoUrl(coordinate, resolution, projection, params);
        console.log('getFeatureInfoUrl', url);
        if (url != undefined && url != null) {
          const r = await axios.get(url);
          r.data.features.forEach(feature => {
            callback(feature, layer);
          });
        }
      }
    });
  }


};

export default DotMapSolarContext;
